import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v } from "../../util";
import useCompany from "../Company/use-company";
import * as comunCss from "../comum_css/index";
import { Container, Content } from "./styles";
import { useToast } from "../toast-provider";

interface FormData {
  name: string;
  email: string;
  sendInvite: boolean;
  plataform: number;
  active: boolean;
  workspaceQuota: number;
  userQuota: number;
  botQuota: number;
  allowCampaignCreation: boolean;
  whatsappQrcode: boolean;
  whatsappApiMeta: boolean;
  livechat: boolean;
  facebook: boolean;
  instagram: boolean;
  pipeline: boolean;
  calendar: boolean;
}

const EditCompany = ({
  visible,
  onHide,
  id
}: {
  visible: boolean;
  onHide: () => void;
  id: number;
}) => {
  const [company, setCompany] = useState<FormData>();
  const [plataforms, setPlataforms] = useState<{ id: number; name: string }[]>(
    []
  );
  const { findPlataforms, createNewCompany, searchCompany, updateCompany } =
    useCompany();

  const toastRef = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    shouldFocusError: false,
    defaultValues: {
      name: "",
      email: "",
      sendInvite: false,
      plataform: undefined,
      active: false,
      workspaceQuota: 0,
      userQuota: 0,
      botQuota: 0,
      allowCampaignCreation: false,
      whatsappQrcode: false,
      whatsappApiMeta: false,
      livechat: false,
      facebook: false,
      instagram: false,
      pipeline: false,
      calendar: false
    },
    resolver: zodResolver(
      v.object({
        name: v.string().superRefine(v.nonempty()),
        email: v.string().superRefine(v.nonempty()),
        sendInvite: v.boolean(),
        plataform: v.number(),
        active: v.boolean(),
        workspaceQuota: v.number(),
        userQuota: v.number(),
        botQuota: v.number(),
        allowCampaignCreation: v.boolean(),
        whatsappQrcode: v.boolean(),
        whatsappApiMeta: v.boolean(),
        livechat: v.boolean(),
        facebook: v.boolean(),
        instagram: v.boolean(),
        pipeline: v.boolean(),
        calendar: v.boolean()
      })
    )
  });

  useEffect(() => {
    const fetchPlataforms = async () => {
      const result = await findPlataforms();
      setPlataforms(result);
    };

    fetchPlataforms();
  }, []);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (id) {
        const result = await searchCompany(id);
        if (result) {
          const platform = plataforms.find(
            (p) => p.name === result.plataformName
          );

          reset({
            ...result,
            plataform: platform ? platform.id : 0,
            whatsappQrcode: result.whatsappQrcode ?? false,
            whatsappApiMeta: result.whatsappApiMeta ?? false,
            livechat: result.livechat ?? false,
            facebook: result.facebook ?? false,
            instagram: result.instagram ?? false,
            pipeline: result.pipeline ?? false,
            calendar: result.calendar ?? false
          });
        }
      } else {
        reset({
          name: "",
          email: "",
          sendInvite: false,
          plataform: undefined,
          active: false,
          workspaceQuota: 0,
          userQuota: 0,
          botQuota: 0,
          allowCampaignCreation: false,
          whatsappQrcode: false,
          whatsappApiMeta: false,
          livechat: false,
          facebook: false,
          instagram: false,
          pipeline: false,
          calendar: false
        });
      }
    };

    fetchCompanyData();
  }, [id, reset, visible, plataforms]);

  return (
    <Sidebar
      className="wsDialog"
      style={{ width: "380px" }}
      visible={visible}
      icons={id ? "Editar Empresa" : "Criar Empresa"}
      position="right"
      dismissable={true}
      onHide={() => {
        reset(undefined);
        setCompany(undefined);
        onHide();
      }}
    >
      <Container>
        <Content>
          <div>
            <label css={comunCss.inputLabelDefault} htmlFor="userName">
              Nome
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <InputText {...field} className="w-full" id="name" />
              )}
            />
            {errors.name?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.name.message}</div>
            ) : null}
          </div>

          <div>
            <label css={comunCss.inputLabelDefault} htmlFor="email">
              E-mail
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <InputText {...field} className="w-full" id="email" />
              )}
            />
            {errors.email?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.email.message}</div>
            ) : null}
          </div>

          <div>
            <Controller
              name="sendInvite"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  id="sendInvite"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.checked)}
                />
              )}
            />
            Enviar convite
          </div>

          <div>
            <Controller
              name="plataform"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  id="plataform"
                  onChange={(e) => field.onChange(e.value)}
                  options={plataforms}
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Selecione a plataforma"
                  className="w-full"
                />
              )}
            />
            {errors.plataform?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.plataform.message}</div>
            ) : null}
          </div>

          <div>
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  id="active"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.checked)}
                />
              )}
            />
            Ativo
          </div>

          <div>
            <label css={comunCss.inputLabelDefault} htmlFor="workspaceQuota">
              Limite de workspaces (0 para ilimitadas)
            </label>
            <Controller
              name="workspaceQuota"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  className="w-full"
                  id="workspaceQuota"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            {errors.name?.message ? (
              <div style={{ color: "#AA3939" }}>
                {errors.workspaceQuota?.message}
              </div>
            ) : null}
          </div>

          <div>
            <label css={comunCss.inputLabelDefault} htmlFor="userQuota">
              Limite de usuários (0 para ilimitados)
            </label>
            <Controller
              name="userQuota"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  className="w-full"
                  id="userQuota"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            {errors.name?.message ? (
              <div style={{ color: "#AA3939" }}>
                {errors.userQuota?.message}
              </div>
            ) : null}
          </div>

          <div>
            <label css={comunCss.inputLabelDefault} htmlFor="userQuota">
              Limite de bots (0 para ilimitados)
            </label>
            <Controller
              name="botQuota"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  className="w-full"
                  id="botQuota"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            {errors.name?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.botQuota?.message}</div>
            ) : null}
          </div>

          <div style={{ marginTop: "10px" }}>
            <Controller
              name="allowCampaignCreation"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  id="allowCampaignCreation"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.checked)}
                />
              )}
            />
            Permitir a criação de campanhas
          </div>

          <div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="pipeline"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="pipeline"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              Pipeline
            </div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="calendar"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="calendar"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              Calendario
            </div>
          </div>

          <div>
            <div>Gerenciamento de canais</div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="whatsappQrcode"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="whatsappQrcode"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              WhatsApp QRCode
            </div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="whatsappApiMeta"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="whatsappApiMeta"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              WhatsApp API Meta
            </div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="livechat"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="livechat"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              LiveChat
            </div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="facebook"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="facebook"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              Facebook
            </div>
            <div style={{ marginTop: "10px" }}>
              <Controller
                name="instagram"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="instagram"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              Instagram
            </div>
          </div>

          <Button
            title={id ? "Atualizar empresa" : "Criar empresa"}
            onClick={handleSubmit(async (data) => {
              try {
                if (id) {
                  await updateCompany(id, data);

                  toastRef.current?.show({
                    severity: "success",
                    detail: "Empresa atualizada com sucesso!"
                  });
                } else {
                  await createNewCompany(data);

                  toastRef.current?.show({
                    severity: "success",
                    detail: "Empresa cadastrada com sucesso!"
                  });
                }

                reset({
                  name: "",
                  email: "",
                  sendInvite: false,
                  plataform: undefined,
                  active: false,
                  workspaceQuota: 0,
                  userQuota: 0,
                  botQuota: 0,
                  allowCampaignCreation: false,
                  whatsappQrcode: false,
                  whatsappApiMeta: false,
                  livechat: false,
                  facebook: false,
                  instagram: false,
                  pipeline: false,
                  calendar: false
                });

                onHide();
              } catch (e: any) {
                toastRef.current?.show({
                  severity: "error",
                  detail: e.message || "Ocorreu um erro ao salvar a empresa."
                });
              }
            })}
            css={comunCss.saveBtn}
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px"
            }}
            className="saveBTN  p-button-lg mt-3 p-button-text w-full text-blue-800"
          >
            <i className="pi pi-check-circle" />
            {id ? "ATUALIZAR" : "CRIAR"}
          </Button>
        </Content>
      </Container>
    </Sidebar>
  );
};

export default EditCompany;
